import type { PillIntent } from '@/_components';
import type { IconType } from '@/_types/icons';
import {
  DeliverFilterObject,
  TaxReturnStatusType,
  TaxReturnDeliveryColumnType,
  ClassificationPageSectionType,
  ClassificationOptionType,
} from '@/_types/taxReturnDelivery';

export const TAX_RETURN_INTERVAL = 5000 as const;
export interface TaxReturnStatus {
  icon: IconType;
  intent: PillIntent;
  text: string;
  description: string;
  value: TaxReturnStatusType;
}

export const DELIVER_TABLE_DEFAULT_SORTING = [
  {
    id: 'updated_at',
    desc: true,
  },
];

export const DELIVER_STATUS_TYPES = [
  'action_required',
  'error',
  'filed',
  'finished',
  'invoice_paid',
  'processing',
  'sending',
  'sent',
  'signed',
  'draft',
  'change_requested',
  'approved',
];

export const TAX_RETURN_DELIVERY_PATH =
  '/_soraban/Tax Return Delivery' as const;

export const TAX_RETURN_TABLE_HEADER_MAP: Record<
  TaxReturnDeliveryColumnType,
  string
> = {
  select: 'Select',
  company_name: 'Entity Name',
  tax_year: 'Year',
  tax_return_name: 'Return Name',
  current_status: 'Status',
  signatures: 'Signatures',
  payments: 'Payments',
  updated_at: 'Last Activity',
  last_signed_at: 'Last Signed',
  last_reminder_sent_at: 'Last Reminder',
  assigned_advisor: 'Assigned Firm User',
  reminder_disabled: 'Reminder',
  identity_verification_status: 'ID Fraud Detection',
  k1_distribution_status: 'K-1 Distribution',
  actions: '',
};

export const DEFAULT_TAX_RETURN_COLUMNS: Array<TaxReturnDeliveryColumnType> = [
  'select',
  'company_name',
  'tax_year',
  'tax_return_name',
  'current_status',
  'signatures',
  'payments',
  'updated_at',
  'last_reminder_sent_at',
  'assigned_advisor',
  'reminder_disabled',
  'identity_verification_status',
  'k1_distribution_status',
  'actions',
];

export const DEFAULT_DELIVER_FILTER: DeliverFilterObject = {
  fraud_detection_status: [],
  k1_distribution_status: [],
  payments_made: '',
  reminder_disabled: '',
  reminder_sent: '',
  search_term: '',
  signatures_signed: '',
  status: [],
  voucher_payments_made: '',
  year: '',
};

export const DELIVER_STATUS_TEXT_MAP: Record<TaxReturnStatusType, string> = {
  action_required: 'Action Required',
  change_requested: 'Change Requested',
  approved: 'Approved',
  draft: 'Draft',
  completed: 'Completed',
  error: 'Error',
  filed: 'Filed',
  finished: 'Finished',
  importing: 'Importing',
  invoice_paid: 'Invoice Paid',
  paid: 'Paid',
  processing: 'Processing',
  sending: 'Sending',
  sent: 'Sent',
  signed: 'Signed',
};

export const TAX_RETURN_STATUSES: Array<TaxReturnStatus> = [
  {
    value: 'filed',
    text: 'Filed',
    description: 'The tax return has been marked as filed.',
    icon: 'file',
    intent: 'success',
  },
  {
    value: 'sending',
    text: 'Sending',
    description: 'Transition phase between Action Required and Sent.',
    icon: 'email',
    intent: 'default',
  },
  {
    value: 'draft',
    text: 'Draft Sent',
    description:
      'The tax return is in draft, and is awaiting approval by client.',
    icon: 'email',
    intent: 'default',
  },
  {
    value: 'change_requested',
    text: 'Change requested',
    description:
      'Client has requested changes to the tax return. Review the comment and update the tax return.',
    icon: 'warning',
    intent: 'warning',
  },
  {
    value: 'approved',
    text: 'Approved by client',
    description:
      'Client has approved the tax return. Classify and send to client.',
    icon: 'thumbs-up',
    intent: 'success',
  },
  {
    value: 'sent',
    text: 'Sent',
    description:
      'The return has been successfully sent to the client. It remains in this status until all required forms are signed.',
    icon: 'email',
    intent: 'info',
  },
  {
    value: 'signed',
    text: 'Signed',
    description: 'Tax return signature requests were fulfilled.',
    icon: 'check-circle',
    intent: 'success',
  },
  {
    value: 'completed',
    text: 'Completed',
    description: 'Firm user has marked the return as completed.',
    icon: 'checks',
    intent: 'success',
  },
  {
    value: 'error',
    text: 'Error',
    description:
      'An issue occurred during the upload process, preventing successful processing.',
    icon: 'x',
    intent: 'danger',
  },
  {
    value: 'processing',
    text: 'Processing',
    description:
      'The return has been uploaded but is not yet ready for classification for client delivery.',
    icon: 'circle',
    intent: 'default',
  },
  {
    value: 'action_required',
    text: 'Action Required',
    description:
      'The return has been uploaded, initially processed, and is ready for classification before being sent to the client.',
    icon: 'warning',
    intent: 'warning',
  },
  {
    value: 'paid',
    text: 'Paid',
    description: '',
    icon: 'check',
    intent: 'success',
  },
  {
    value: 'invoice_paid',
    text: 'Invoice Paid',
    description:
      'The client has successfully paid the invoice via Stripe integration in the Payment tab.',
    icon: 'check',
    intent: 'success',
  },
  {
    value: 'importing',
    text: 'Importing',
    description: '',
    icon: 'refresh',
    intent: 'secondary',
  },
  {
    value: 'finished',
    text: 'Finished',
    description:
      "Firm user has marked this as finished. Typically utilized when a client has finished signing and the firm has downloaded all forms for e-filing so they need to be able to see what still requires review and what doesn't.",
    icon: 'check-circle-filled',
    intent: 'success',
  },
];

export const DEFAULT_DELIVERY_MESSAGE_TEMPLATE_SUBJECT =
  'Your {tax_year} tax return is ready for review';

// TODO: Replace <p></p> tags with \n for react when migration is complete
export const DEFAULT_DELIVERY_MESSAGE_TEMPLATE_BODY =
  '<p>Hi {client_preferred_name},</p><p>We are pleased to inform you that your {tax_year} Tax Return is now available for your review. Kindly take a moment to review and sign the return by clicking on the link below. The link will also display any outstanding balance owed to the tax authority.</p><p>Once we receive the e-file authorization form (and payment), we will proceed with e-filing the tax return.</p><p>Thank you,</p><p>{firm_name}</p>';

export const DELIVERY_CLASSIFICATION_DEFAULTS: ClassificationPageSectionType = {
  e_sign_forms: [],
  filing_instructions: [],
  paper_filed_returns: {},
  payment_vouchers: [],
  tax_return_pages: [],
  transmittal_letters: [],
};

export const DELIVERY_CLASSIFICATION_OPTIONS: Array<ClassificationOptionType> =
  [
    {
      name: 'Tax Return Page',
      key: 'tax_return_pages',
      color: 'blue',
      description:
        'Enter the page numbers/ranges for this client to review before e-signing.',
      options: { has_watermark: true },
    },
    {
      name: 'E-Sign Form',
      key: 'e_sign_forms',
      color: 'green',
      description:
        'Enter the page numbers/ranges for this client to e-sign after reviewing the Tax Return Pages.',
    },
    {
      name: 'Payment Voucher',
      key: 'payment_vouchers',
      color: 'brown',
      description:
        'Enter the page numbers/ranges of all payment vouchers for this client.',
    },
    {
      name: 'Filing Instruction',
      key: 'filing_instructions',
      color: 'purple',
      description:
        'Enter the page numbers/ranges for the filing instructions. These pages will NOT be viewable by clients. You can change this setting in firm settings under Preferences',
    },
  ];

export const REMINDER_DISABLED_REASONS: Record<string, string> = {
  all_signatures_signed_and_all_vouchers_paid:
    'Reminder disabled when all signatures are signed and all vouchers are paid.',
  action_required: 'Reminder disabled when return is not delivered.',
};

export const REMINDER_DEFAULT_MESSAGES: Record<string, string> = {
  email_subject:
    '[Reminder] Actions required for completing your {tax_year} tax filing.',
  email_message:
    'Hi {client_fname},\nThis is a reminder to complete your {tax_year} tax return ({tax_return_name}). Kindly use the button below to open the tax return.',
  // Not used yet
  // in_app_message:
  //   'This is a reminder to complete your {tax_year} tax return ({tax_return_name}).',
  // sms_message:
  //   '{firm_name}: Hi there, this is a reminder to complete your {tax_year} tax return ({tax_return_name}).',
};
